/* eslint-disable react/jsx-curly-brace-presence */
import { ExclamationOutlined, MoreOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@apollo/client";
import { Dropdown, Empty, message, Modal, Skeleton, Tooltip } from "antd";
import { capitalize } from "lodash";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DELETE_LEAD_TASK, UPDATE_LEAD_TASK } from "../../../graphql/mutation";
import { GET_LEAD_TASKS } from "../../../graphql/query";
import { useGetOpportunityId } from "../../../hooks/crmIntegrationHook";
import { convertUtcToLocal } from "../../../library/utils";
import { deleteTask, reopenTask, setTasks } from "../../../redux/slices/taskSlice";
import { snakeCaseToTitleCase } from "../../../utils/cases";
import { checkAccessPermission } from "../../../library/helpers";
import { AccessPermissionAction } from "../../../library/constants";
import useCrm from "../../../hooks/useCrm";

export const LeadTaskList = ({ selectedLead, setleadTaskData }) => {
  const dispatch = useDispatch();
  const { tasks, count } = useSelector(state => state.tasks);
  const taskStatus = [
    { value: "ALL", label: "All" },
    { value: "CREATED", label: "Upcoming" },
    { value: "COMPLETED", label: "Completed" },
    { value: "OVERDUE", label: "Overdue" },
  ];
  const [selectedStatus, setselectedStatus] = useState([]);
  const { loading: leadTaskLoader, refetch: refetchLeadTask } = useQuery(GET_LEAD_TASKS, {
    variables: {
      leadId: +selectedLead.id,
      taskTypes: [],
      taskStatuses: selectedStatus,
    },
    fetchPolicy: "network-only",
    onCompleted: res => {
      dispatch(setTasks({ tasks: res.getLeadTasks.leadTasks, count: res.getLeadTasks.count }));
    },
  });
  const refetchTasks = async () => {
    const res = await refetchLeadTask();
    dispatch(setTasks(res.getLeadTasks));
  };
  return (
    <div className="flex-col-centered items-start gap-1">
      <div className="flex-row-centered gap-2">
        <span className="sm-400 text-grey-500">Filter By:</span>
        {/* <Dropdown menu={{ items: [] }}> */}
        <span className="sm-400 flex-row-centered gap-1 cursor-pointer text-brand-500">
          All Tasks
          {/* <DownIcon color="#3B82F6" /> */}
        </span>
        {/* </Dropdown> */}
      </div>
      {/* Status filters */}
      <div className="flex-row-centered gap-2">
        {taskStatus.map((item, index) => (
          <div
            className={`px-2 py-1 rounded-sm cursor-pointer xs-400 text-grey-700 ${
              selectedStatus.includes(item.value) ? "bg-grey-200" : "bg-grey-50"
            }`}
            key={index}
            onClick={() => {
              if (item.value === "ALL") {
                return setselectedStatus([]);
              }
              setselectedStatus([item.value]);
            }}
          >
            {capitalize(item.label)}
          </div>
        ))}
      </div>
      {/* Task Cards */}
      <div className="flex-col-centered gap-3 w-full">
        <Skeleton loading={leadTaskLoader} active avatar>
          {tasks && tasks.length > 0 ? (
            tasks
              .filter(item => {
                if (selectedStatus.length === 0) {
                  return item;
                }
                if (item.taskStatus === selectedStatus[0]) {
                  return item;
                }
              })
              .map((item, index) => (
                <TaskCard
                  item={item}
                  key={index}
                  setleadTaskData={setleadTaskData}
                  selectedLead={selectedLead}
                  refetchLeadTask={refetchTasks}
                />
              ))
          ) : (
            <div className="flex-row-centered justify-center w-full">
              <Empty description="Please add task for this lead" />
            </div>
          )}
        </Skeleton>
      </div>
    </div>
  );
};

const TaskCard = ({ item, setleadTaskData, selectedLead, refetchLeadTask }) => {
  const { getOpportunityId, crmTypeName } = useCrm();
  const canDeleteTask = checkAccessPermission(AccessPermissionAction.TASK_MANAGEMENT_DELETE);
  const canEditTask = checkAccessPermission(AccessPermissionAction.TASK_MANAGEMENT_ADD_EDIT);

  const dispatch = useDispatch();
  const handleEdit = (isCompleted = false) => {
    setleadTaskData({
      open: true,
      mode: "EDIT",
      leadTask: item,
      selectedLead: selectedLead,
      isCompleted,
    });
  };

  const [updateLeadTask, { loading: updateLeadLoader }] = useMutation(UPDATE_LEAD_TASK);
  const [deleteLeadTask] = useMutation(DELETE_LEAD_TASK);
  const isTaskCompleted = record => record.taskStatus === "COMPLETED";
  const opportunityId = useGetOpportunityId(item?.lead?.leadOpportunities[0]);
  const CompletionTooltip = () => {
    return (
      <div className="flex-col-centered gap-2">
        <div className="flex-row-centered justify-between gap-3">
          <span>Completed on:</span>
          <span>{convertUtcToLocal(item.completionDate, "MMM DD, YYYY, hh:mm A")}</span>
        </div>
        <div className="flex-row-centered justify-between gap-3">
          <span>Completed note:</span>
          <span>{item?.completionNote || ""}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="flex-col-centered gap-4 rounded border border-solid border-grey-200">
      <div className="p-3 pb-0 flex-col-centered items-start">
        <Tooltip title={item.taskStatus === "COMPLETED" ? <CompletionTooltip /> : null}>
          <span
            className={`sm-600 text-brand-500 ${item.taskStatus === "COMPLETED" && "line-through"}`}
          >
            {item.title}
          </span>
        </Tooltip>
        <span className="xs-400 text-grey-500">
          {capitalize(snakeCaseToTitleCase(item.taskType))} {crmTypeName}{" "}
          {getOpportunityId(item?.lead?.leadOpportunities, item.leadOpportunityId)}
        </span>
      </div>
      <div className="flex-row-centered px-3 pt-0 pb-2 justify-between items-end">
        <div className="flex-col-centered">
          <span className="text-xs text-grey-700">
            <b>Assigned to:</b> {item?.assignedTo?.fullName || ""}
          </span>
          {!isTaskCompleted(item) ? (
            <span className="xs-400 text-grey-700">
              Due on {convertUtcToLocal(item.dueDate, "MMM DD, YYYY | hh:mm A")}
            </span>
          ) : (
            <span className="xs-400 text-grey-700">
              Completed on {convertUtcToLocal(item.completionDate, "MMM DD, YYYY | hh:mm A")}
            </span>
          )}
        </div>
        <Dropdown
          menu={{
            items: [
              // { label: "View" },
              { label: "Edit", onClick: () => handleEdit(), disabled: !canEditTask },
              // { label: "Send Reminder", onClick: () => handleEdit() },
              ...(item.taskStatus === "COMPLETED"
                ? [
                    {
                      label: "Mark as open",
                      disabled: !canEditTask,
                      onClick: () => {
                        Modal.confirm({
                          title: "Are you sure you want to open this task again?",
                          onOk: async () => {
                            const res = await updateLeadTask({
                              variables: {
                                id: item.id,
                                taskStatus: "CREATED",
                              },
                            });
                            if (res.data.updateLeadTask.statusCode === 200) {
                              message.success("Lead task re-opened successfully!");
                              dispatch(reopenTask(item.id));
                            } else {
                              message.error(res.data.updateLeadTask.message);
                            }
                          },
                        });
                      },
                    },
                  ]
                : [
                    {
                      label: "Mark as completed",
                      disabled: !canEditTask,
                      onClick: () => handleEdit(true),
                    },
                  ]),
              {
                label: "Delete",
                danger: true,
                disabled: !canDeleteTask,
                onClick: () =>
                  Modal.confirm({
                    title: "Delete this task?",
                    icon: <ExclamationOutlined />,
                    content: "Are you sure you want to delete this task?",
                    onOk: async () => {
                      const res = await deleteLeadTask({
                        variables: {
                          taskIds: [+item.id],
                        },
                      });
                      if (res.data.deleteLeadTask.statusCode === 200) {
                        message.success("Task deleted successfully");
                        dispatch(deleteTask(item.id));
                      }
                    },
                    okButtonProps: {
                      loading: updateLeadLoader,
                    },
                    onCancel: () => {},
                  }),
              },
            ],
          }}
          placement="topLeft"
        >
          <div className="p-1 cursor-pointer rounded-sm border border-solid border-grey-200 flex-row-centered items-center justify-center">
            <MoreOutlined />
          </div>
        </Dropdown>
      </div>
    </div>
  );
};
